import moment from "moment-timezone"
import { repeatProcessData } from "../data"
import { PROCESS_REPEAT, JOB_PINNING } from "../data/enums"
import { useAuth } from "../services"
import { toId } from "./useDataUtils"
import { useDateUtils } from "./useDateUtils"

const { NEVER, DAILY, WEEKLY, ADHOC } = PROCESS_REPEAT

const getRepeatProcessTypeData = (type) => repeatProcessData.find((t) => t.id === type)

const getRepeatDisplayText = (process) => {
  if (process) {
    const type = getRepeatProcessTypeData(process.repeat.type)
    const days = type.id === WEEKLY ? type.data.filter((t) => process.repeat.weekdays[t.id]) : ""
    const frequency = process.repeat.frequency
    const showFrequency = frequency > 1

    return `${showFrequency ? `${frequency} ` : ""}${showFrequency ? type.plural : type.label}${
      days && ` on ${days.map((day) => (days.length > 3 ? day.label.substring(0, 3) : day.label)).join(", ")}`
    }`
  }
}

const getRepeatDisplayShortText = (process) => {
  if (process) {
    const type = getRepeatProcessTypeData(process.repeat.type)
    const days = type.id === WEEKLY ? type.data.filter((t) => process.repeat.weekdays[t.id]) : ""
    const frequency = process.repeat.frequency
    const showFrequency = frequency > 1

    if (type.id === WEEKLY && days?.length === 1) {
      return `${days[0].label}s`
    }
    if (type.id === WEEKLY && days?.length === 2) {
      return days.map(({ shortLabel }) => shortLabel).join(" & ")
    }
    if (type.id === WEEKLY && days?.length === 7) {
      return getRepeatProcessTypeData(DAILY).label
    }

    return `${showFrequency ? `${frequency} ` : ""}${showFrequency ? type.plural : type.label}${
      days.length ? ` (${days.length} day${days.length > 1 ? "s" : ""})` : ""
    }`
  }
}

const getFriendlySetName = (set) => {
  if (set === "active-recurring") return "Active recurring processes"
  if (set === "active-oneoff") return "Active one-off processes"
  if (set === "active-adhoc") return "Active ad hoc processes"
  if (set === "active-tasks") return "Active tasks"
  if (set === "inactive-recurring") return "Inactive recurring processes"
  if (set === "inactive-oneoff") return "Inactive one-off processes"
  if (set === "inactive-adhoc") return "Inactive ad hoc processes"
  if (set === "inactive-tasks") return "Inactive tasks"
  return "Unknown set"
}

const canScheduleHaveNextInstancePinningType = (type) => ![NEVER, DAILY, ADHOC].includes(type)

const isPeriodProcessPinningType = (type) => [JOB_PINNING.MINUTES, JOB_PINNING.HOURS, JOB_PINNING.DAYS].includes(type)

const getProcessPinningTypeDescription = ({ pinning }) => {
  if (!pinning) {
    return null
  }
  const { type, period } = pinning

  switch (type) {
    case JOB_PINNING.COMPLETED:
      return "Until completed"
    case JOB_PINNING.DAYS:
      return `${period} days`
    case JOB_PINNING.HOURS:
      return `${period} hours`
    case JOB_PINNING.MINUTES:
      return `${period} minutes`
    case JOB_PINNING.NEXT_INSTANCE:
      return "Until next instance"
    default:
      return null
  }
}

const useProcessUtils = () => {
  const { hasTime } = useDateUtils()
  const { hasFeature } = useAuth()

  const canScheduleOnlyPinPeriodTypes = (type) => hasFeature("process_pinuntil") && [DAILY, WEEKLY].includes(type)

  const getScheduleDisplayText = (process) => {
    if (process && process.repeat) {
      const type = getRepeatProcessTypeData(process.repeat.type)
      if (process.repeat.type === ADHOC) return type.label

      let { frequency, weekdays, months } = process.repeat

      months = Object.keys(months).filter((month) => month !== "__typename" && months[month])

      const after = moment(process.after)
      const days = type.id === WEEKLY ? type.data.filter((t) => weekdays[t.id]) : ""
      const allDay = !hasTime(process.after)

      const forStart = `Starts ${after.format("dddd Do MMM YYYY")}${!allDay ? " at " : ""}${
        !allDay ? after.format("h:mmA") : ""
      }`

      const forFrequency = frequency > 1 ? `every ${frequency} ${type.plural.toLowerCase()}` : type.label.toLowerCase()
      const forRepeat = `${type.id !== NEVER ? "then " : ""}${forFrequency}`

      const forDays = days
        ? `${days && ` on ${days.map((day) => (days.length > 3 ? day.label.substring(0, 3) : day.label)).join(", ")}`}`
        : ""

      const forMonths = months.length
        ? ` in ${months.map((month) => `${month.substring(0, 1).toUpperCase()}${month.substring(1, 3)}`).join(", ")}`
        : ""

      return `${forStart}, ${forRepeat}${forDays}${forMonths}`
    }
  }

  const getNavigateToLink = (template, set, action) => `/templates/${set}/${toId(template)}/${action}`

  const getNavigateToTemplateLink = (template, set, category, action) => {
    if (category) return `/templates/${set || template.statusSet}/${category}/${toId(template)}/${action}`
    return `/templates/${set || template.statusSet}/${toId(template)}/${action}`
  }

  return {
    canScheduleOnlyPinPeriodTypes,
    canScheduleHaveNextInstancePinningType,
    isPeriodProcessPinningType,

    getNavigateToLink,
    getNavigateToTemplateLink,
    getRepeatProcessTypeData,
    getRepeatDisplayText,
    getRepeatDisplayShortText,
    getScheduleDisplayText,
    getProcessPinningTypeDescription,
    getFriendlySetName,
  }
}

export { useProcessUtils, getRepeatDisplayText, getRepeatDisplayShortText }
